import { useCallback } from 'react'
import { convertToFormDateTime, convertToISODateTime } from 'shared/util'

import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import { Button, Stack, TextField } from '@mui/material'

import { Section } from 'components/controls/section'
import { MarkdownInput } from 'components/controls/markDownInput'

import { AgreementFormData, AgreementResult } from "interface/agreement"

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

const {
  Required,
  TooLongLength,
  TooShortLength
} = validationMessages

const formSchema = yup.object().shape({
  title: yup.string().required(Required)
		.min(1, TooShortLength(1))
		.max(200, TooLongLength(200)),
  content: yup.string().required(Required),
  enableTime: yup.string().nullable()
})

type useAgreementFormProps = {
	agreement: AgreementResult | null
	onSubmit: (form: AgreementFormData) => void
}
export const useAgreementForm = ({
	agreement,
	onSubmit
}: useAgreementFormProps) => {
	const form = useForm({
		defaultValues: agreement ? {
			title: agreement.title,
			content: agreement.content,
			enableTime: convertToFormDateTime(agreement.enableTime)
		} : {
			title: '',
			content: '',
			enableTime: undefined
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
    submit: form.handleSubmit(input => onSubmit({
			title: input.title ?? '',
			content: input.content ?? '',
			enableTime: convertToISODateTime(input.enableTime)
		}))
	}
}

export const AgreementForm = ({
	control,
	setValue,
	formState: { errors }
}: ReturnType<typeof useAgreementForm>) => {
	const clearEnableTime = useCallback(
		() => {
			setValue("enableTime", null)
		},
		[setValue]
	)
	return (
		<Stack direction="row" spacing={2} height="100%" overflow="hidden">
			<Stack direction="column" flexGrow={1} overflow="hidden">
				<Section label="タイトル" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="title"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.title}
								helperText={errors.title ? errors.title.message : ''}
							/>
						)}
					/>
				</Section>

				<Section label="適用開始" labelProps={{ py: 2 }} disablePaddingY>
					<Stack direction="row" spacing={1}>
						<Controller
							name="enableTime"
							control={control}
							rules={{
								required: false,
								validate: (value) => {
									const formatDate = dayjs(value).format('YYYY/MM/DD HH:mm')
									if (!dayjs(formatDate).isValid()) {
										return '日時形式が間違っています'
									}
								}
							}}
							render={({ field, formState: { errors } }) => (
								<LocalizationProvider
									dateAdapter={AdapterDayjs}
									adapterLocale="ja"
								>
									<DesktopDatePicker
										{...field}
										format="YYYY/MM/DD HH:mm"
										value={dayjs(field.value)}
										slotProps={{
											textField: {
												size: "small",
												variant: "outlined",
												error: !!errors.enableTime,
												helperText: errors.enableTime ? errors.enableTime.message : ''
											}
										}}
									/>
								</LocalizationProvider>
							)}
						/>
						<Button
							size="small"
							onClick={clearEnableTime}
						>
							クリア
						</Button>
					</Stack>
				</Section>

				<Section label="内容" labelProps={{ py: 2 }} flexGrow={1} overflow="hidden">
					<Controller
						control={control}
						name="content"
						render={({ field }) => (
							<MarkdownInput
								{...field}
								variant="outlined"
								size="small"
								fullHeight={true}
								error={!!errors.content}
								helperText={errors.content ? errors.content.message : ''}
							/>
						)}
					/>
				</Section>
			</Stack>
		</Stack>
	)
}